import { esc, row } from "./html"
import { CLIENT_CONTRACT } from "./client_contract"
import { MANAGED_TRAIL_LAYERS } from "./map_layer_view_policy"
import { metersToMiles } from "./web_location_policy"

export const MAP_POPUP_MAX_WIDTH = "320px"
export const PIN_POPUP_OFFSET = 18
export const TRAIL_START_POPUP_OFFSET = 24

// MapLibre dispatches marker clicks through the map's click event before the
// marker toggles its own popup. The underlying feature handler must leave those
// clicks alone; replacing the clicked marker mid-dispatch detaches its map and
// makes Popup.addTo receive undefined.
export function mapOverlayOwnsClick(target) {
  return !!target?.closest?.(".maplibregl-marker, .maplibregl-popup, .maplibregl-ctrl")
}

// Popup HTML builders for the trail map's overlay layers. Fields come pre-shaped
// from the server; everything is escaped before interpolation. Extracted from
// map_controller so the controller stays orchestration.

// Agency trail/route: name, source, surface, allowed uses, motorized flag.
export function usfsPopup(p) {
  const allowed = Object.entries(p.uses || {}).filter(([, v]) => v).map(([k]) => k)
  let html = `<span class="font-semibold block mb-1">🥾 ${esc(p.name)}</span>`
  if (p.source) html += `<span class="block text-xs text-slate-500 mb-1">${esc(p.source)}</span>`
  if (p.surface) html += row("Surface", p.surface)
  if (p.access) html += row("Access", p.access)
  if (p.season) html += row("Season", p.season)
  if (p.distance_m) html += row("Distance", `${metersToMiles(p.distance_m).toFixed(1)} mi`)
  html += row("Allows", allowed.length ? allowed.join(", ") : "—")
  html += p.motorized ? `<span style="color:#e65100">Motorized</span>` : `<span style="color:#1b5e20">Non-motorized</span>`
  return html
}

// OpenStreetMap trail: type/use lines, distance, difficulty, and OSM link.
export function trailPopup(p) {
  let html = `<span class="font-semibold block mb-1">${esc(p.title || `🥾 ${p.name || "Trail"}`)}</span>`
  for (const line of p.lines || []) html += `${esc(line)}<br/>`
  if (p.distance_m) html += row("Distance", `${metersToMiles(p.distance_m).toFixed(1)} mi`)
  if (p.start) {
    const label = `${formatCoord(p.start.lat)}, ${formatCoord(p.start.lon)}`
    html += `OSM start: <a href="${esc(googleMapsUrl(p.start))}" target="_blank" rel="noopener">${esc(label)}</a><br/>`
  }
  if (p.node_count) html += `Nodes: ${esc(p.node_count)}<br/>`
  html += osmTagTable(p.osm_tags)
  if (p.link) html += `<a href="${esc(p.link.href)}" target="_blank" rel="noopener">${esc(p.link.text)}</a>`
  return html
}

function osmTagTable(tags) {
  const entries = Object.entries(tags || {})
  if (!entries.length) return ""

  let html = `<div class="mt-2 mb-2 text-xs border-t border-slate-200 pt-2">`
  for (const [key, value] of entries) {
    html += `<div><span class="text-slate-500">${esc(key)}</span>: ${esc(value)}</div>`
  }
  return `${html}</div>`
}

function formatCoord(value) {
  return Number(value).toFixed(5)
}

function googleMapsUrl({ lat, lon }) {
  return `${CLIENT_CONTRACT.externalLinks.googleMapsSearchUrl}?api=1&query=${encodeURIComponent(`${lat},${lon}`)}`
}

// Land-ownership parcel: owner, acreage, assessed value, address, type, legal.
export function parcelPopup(p) {
  let html = `<span class="font-semibold block mb-1">🏷️ ${esc(p.owner)}</span>`
  if (p.acres) html += `${esc(Number(p.acres).toLocaleString())} acres<br/>`
  if (p.value) html += `Assessed: $${esc(Number(p.value).toLocaleString())}<br/>`
  if (p.address) html += `${esc(p.address)}<br/>`
  if (p.ptype) html += `Type: ${esc(p.ptype)}<br/>`
  if (p.legal) html += `<span class="text-xs text-slate-500 block mt-1">${esc(p.legal)}</span>`
  return html
}

export function publicLandPopup(p) {
  let html = `<span class="font-semibold block mb-1">🏞️ ${esc(p.name || "Public land")}</span>`
  if (p.owner_type_label) html += row("Ownership", p.owner_type_label)
  if (p.owner_name) html += row("Owner", p.owner_name)
  if (p.manager_name) html += row("Manager", p.manager_name)
  if (p.designation) html += row("Designation", p.designation)
  if (p.public_access_label) html += row("Public access", p.public_access_label)
  if (p.acres) html += row("Area", `${Number(p.acres).toLocaleString()} acres`)
  html += `<span class="text-xs text-slate-500 block mt-1">Verify current access rules with the managing agency.</span>`
  return html
}

// Wildlife Management Area: name, acreage, hunting/camping/access, FWP link.
export function wmaPopup(p) {
  let html = `<span class="font-semibold block mb-1">🦌 ${esc(p.name)}</span>`
  if (p.acres) html += `${esc(Number(p.acres).toLocaleString())} acres<br/>`
  if (p.hunting) html += row("Hunting", p.hunting)
  if (p.camping) html += row("Camping", p.camping)
  if (p.access) html += row("Access", p.access)
  if (p.link) html += `<a href="${esc(p.link)}" target="_blank" rel="noopener">FWP page</a>`
  return html
}

// Generic server-shaped marker (USGS / OSM trails / rec areas): title, lines, link.
export function markerPopup(m) {
  let html = `<span class="font-semibold block mb-1">${esc(m.title)}</span>`
  for (const line of m.lines || []) html += `${esc(line)}<br/>`
  if (m.link) html += `<a href="${esc(m.link.href)}" target="_blank" rel="noopener">${esc(m.link.text)}</a>`
  return html
}

export function featurePopupPlan(key, properties = {}) {
  if (key === "trails") return { html: trailPopup(properties), selectTrail: true }
  if (key === "rivers") return { html: `<span class="font-semibold">🌊 ${esc(properties.name)}</span>` }
  if (MANAGED_TRAIL_LAYERS.includes(key)) return { html: usfsPopup(properties) }
  if (key === "wma") return { html: wmaPopup(properties) }
  if (key === "public_lands") return { html: publicLandPopup(properties) }
  if (key === "parcels") return { html: parcelPopup(properties) }
  return null
}

export function pinMarkerPlan(key, marker = {}) {
  const siteEvent = key === "usgs" && marker.site
    ? { site: marker.site, title: marker.title }
    : null

  return {
    elementHtml: `<div class="pin ${key}"></div>`,
    cursor: "pointer",
    anchor: "bottom",
    lngLat: [marker.lon, marker.lat],
    siteEvent,
    popupHtml: siteEvent ? null : markerPopup(marker),
    popupOptions: siteEvent ? null : { offset: PIN_POPUP_OFFSET, maxWidth: MAP_POPUP_MAX_WIDTH }
  }
}

export function trailStartMarkerPlan(feature = {}) {
  const properties = feature.properties || {}
  const start = properties.start
  if (!start) return null

  return {
    elementHtml: '<div class="trail-start-button">Start</div>',
    anchor: "bottom",
    lngLat: [start.lon, start.lat],
    popupHtml: trailPopup(properties),
    popupOptions: { offset: TRAIL_START_POPUP_OFFSET, maxWidth: MAP_POPUP_MAX_WIDTH }
  }
}
